import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, Transition as _Transition } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ml-0.5"
}
const _hoisted_2 = { class: "mb-4 relative xl:w-1/2 2xl:mr-3" }
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = { class: "" }
const _hoisted_5 = { class: "flex px-3 items-center justify-between sticky top-0 bg-white z-50" }
const _hoisted_6 = { class: "font-bold py-1" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 0,
  class: "h-4 w-4 rounded bg-yellow-600 text-white ring-1 ring-yellow text-center leading-4 text-sm"
}
const _hoisted_10 = ["id", "checked"]
const _hoisted_11 = ["for"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["id", "value", "checked"]
const _hoisted_14 = ["for"]

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/20/solid";
import { ChevronUpDownIcon } from "@heroicons/vue/24/outline";
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { HierarchyTagStore, HierarchyType } from "shared/types/HierarchyTag";
import { createHierarchyTagContext, tagTypes } from "shared/views/planner/hierarchyTags";
import MainButton from "@/components/other/MainButton.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'LocationFilter',
  props: {
    hierarchyTags: {},
    selected: {},
    placeholder: {},
    defaultOpen: { type: Boolean },
    changeOnSubmit: { type: Boolean },
    popoverClass: {}
  },
  emits: ["change", "userInput"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n();

const internalSelected = ref<Set<string>>(new Set(props.selected));
const searchValue = ref("");
const searchLocationInputElement = ref<HTMLElement | null>(null);
const popoverElement = ref<HTMLDivElement | null>(null);

const hierarchyTagContext = computed(() => createHierarchyTagContext(props.hierarchyTags));

const groupedTags = computed(() => {
  const initialValue = tagTypes.reduce((acc, type) => {
    acc[type] = [];
    return acc;
  }, {} as Record<HierarchyType, HierarchyTagStore[]>);
  return props.hierarchyTags.reduce((acc, tag) => {
    acc[tag.type].push(tag);
    return acc;
  }, initialValue);
});

const groups = computed(() => {
  const sortedGroups = Object.entries(groupedTags.value)
    .filter(([_type, tags]) => tags.length > 0)
    .map(([type, tags]) => ({
      type: type as HierarchyType,
      tags: (tags as HierarchyTagStore[]).filter(
        (tag) =>
          tag.name &&
          (!searchValue.value || tag.name.toLowerCase().includes(searchValue.value.toLowerCase())),
      ),
    }));
  sortedGroups.sort((a, b) => tagTypes.indexOf(a.type) - tagTypes.indexOf(b.type));
  return sortedGroups;
});

const internalSelectedByType = computed(() =>
  tagTypes.reduce((acc, type) => {
    acc[type] = new Set(
      [...internalSelected.value].filter(
        (tagId) => hierarchyTagContext.value.tagsById[tagId]?.type === type,
      ),
    );
    return acc;
  }, {} as Record<HierarchyType, Set<string>>),
);

const checkedAllByType = computed(() =>
  tagTypes.reduce((acc, type) => {
    acc[type] = internalSelectedByType.value[type].size === groupedTags.value[type].length;
    return acc;
  }, {} as Record<HierarchyType, boolean>),
);

const resetSearchBar = () => {
  searchValue.value = "";
};

const emitChange = () => emit("change", [...internalSelected.value]);

const emitChangeImmediatelyIfEnabled = () => {
  if (!props.changeOnSubmit) {
    emitChange();
  }
};

const handleAllOptionsChange = (type: HierarchyType) => {
  const deleteAll = checkedAllByType.value[type];
  groupedTags.value[type].forEach((tag) => {
    deleteAll ? internalSelected.value.delete(tag._id) : internalSelected.value.add(tag._id);
  });
  emitChangeImmediatelyIfEnabled();
};

const handleOptionChange = (tagId: string) => {
  if (internalSelected.value.has(tagId)) {
    internalSelected.value.delete(tagId);
  } else {
    internalSelected.value.add(tagId);
  }
  emitChangeImmediatelyIfEnabled();
};

const clearSelectedInGroup = (type: HierarchyType) => {
  [...internalSelectedByType.value[type]].forEach((tagId) => internalSelected.value.delete(tagId));
  emitChangeImmediatelyIfEnabled();
};

watch(
  () => props.selected,
  () => {
    internalSelected.value = new Set(props.selected);
  },
);

watch(popoverElement, () => {
  if (popoverElement.value) {
    searchLocationInputElement.value?.focus();
  } else {
    resetSearchBar();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Popover), { class: "relative" }, {
    default: _withCtx(({ open }) => [
      (!_ctx.defaultOpen)
        ? (_openBlock(), _createBlock(_unref(PopoverButton), {
            key: 0,
            class: "w-full outline-none"
          }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "default", {}, () => [
                _createElementVNode("template", {
                  class: _normalizeClass([
            'w-full group inline-flex items-center justify-between py-1.5 px-3 bg-white rounded-md text-left shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 sm:text-sm text-xs sm:leading-6  text-gray-400',
            open ? 'ring-2 ring-yellow-500' : '',
          ])
                }, [
                  _createElementVNode("span", null, [
                    _createTextVNode(_toDisplayString(_ctx.placeholder), 1),
                    (_ctx.selected.length > 0)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_1, "(" + _toDisplayString(_ctx.selected.length) + ")", 1))
                      : _createCommentVNode("", true)
                  ]),
                  _createVNode(_unref(ChevronUpDownIcon), {
                    class: "ml-2 h-5 w-5 transition duration-150 ease-in-out",
                    "aria-hidden": "true"
                  })
                ], 2)
              ])
            ]),
            _: 2
          }, 1024))
        : _createCommentVNode("", true),
      _createVNode(_Transition, {
        "enter-active-class": "transition duration-200 ease-out",
        "enter-from-class": "translate-y-1 opacity-0",
        "enter-to-class": "translate-y-0 opacity-100",
        "leave-active-class": "transition duration-150 ease-in",
        "leave-from-class": "translate-y-0 opacity-100",
        "leave-to-class": "translate-y-1 opacity-0"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PopoverPanel), {
            focus: "",
            class: _normalizeClass(["absolute z-50 min-w-max h-max w-max sm:px-0 lg:max-w-5xl overflow-y-scroll xl:overflow-y-hidden PopoverPanelContent", [
          !_ctx.defaultOpen &&
            'md:right-0 lg:left-0 lg:-translate-x-1/4 lg:right-auto md:-translate-x-w-max mt-3',
          _ctx.popoverClass,
        ]]),
            static: _ctx.defaultOpen
          }, {
            default: _withCtx(({ close }) => [
              _createElementVNode("div", {
                class: "bg-gray-50 border border-gray-200 shadow rounded-lg p-4 mb-4",
                ref_key: "popoverElement",
                ref: popoverElement
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "searchLocationInputElement",
                    ref: searchLocationInputElement,
                    type: "text",
                    class: "min-w-[280px] w-full px-3 py-1 text-sm rounded-md border border-gray-300 focus:ring-yellow-500 focus:border-yellow-500",
                    placeholder: _unref(t)('buttons.search'),
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchValue).value = $event))
                  }, null, 8, _hoisted_3), [
                    [_vModelText, searchValue.value]
                  ]),
                  (searchValue.value)
                    ? (_openBlock(), _createBlock(_unref(XMarkIcon), {
                        key: 0,
                        class: "w-4 absolute items-center bottom-0 right-1 top-1.5 text-gray-500",
                        onClick: resetSearchBar
                      }))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", {
                  class: _normalizeClass([[
              {
                'grid-cols-1': groups.value.length === 1,
                'grid-cols-2': groups.value.length === 2,
                'xl:grid-cols-3': groups.value.length === 3,
                'lg:grid-cols-2 xl:grid-cols-4': groups.value.length >= 4,
              },
            ], "sm:grid lg:grid-flow-auto gap-2 items-start"])
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
                    return (_openBlock(), _createElementBlock("div", {
                      class: "overflow-y-auto pl-4 pr-2 lg:max-w-[280px] lg:w-[280px] shadow bg-white h-96 sm:max-w-1/2",
                      key: group.type
                    }, [
                      _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                          _createElementVNode("p", _hoisted_6, _toDisplayString(_unref(t)(`analytics.reports.${group.type}`)), 1),
                          (internalSelectedByType.value[group.type].size > 0)
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 0,
                                onClick: ($event: any) => (clearSelectedInGroup(group.type)),
                                class: "text-gray-500 text-xs hover:underline",
                                type: "button"
                              }, _toDisplayString(_ctx.$t("buttons.clear")), 9, _hoisted_7))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", {
                          class: "py-2 px-3 rounded-md w-full flex items-center hover:bg-yellow-200",
                          onClick: ($event: any) => {
                    handleAllOptionsChange(group.type);
                    checkedAllByType.value[group.type] ? emit('userInput') : '';
                  }
                        }, [
                          (
                      internalSelectedByType.value[group.type].size > 0 && !checkedAllByType.value[group.type]
                    )
                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, " - "))
                            : (_openBlock(), _createElementBlock("input", {
                                key: 1,
                                type: "checkbox",
                                id: `checkbox_tag_all_${group.type}`,
                                checked: checkedAllByType.value[group.type],
                                class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                              }, null, 8, _hoisted_10)),
                          _createElementVNode("label", {
                            for: `checkbox_tag_all_${group.type}`,
                            class: "ml-3 text-sm",
                            onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"]))
                          }, [
                            _createTextVNode(_toDisplayString(_unref(t)("analytics.planner.all")) + " ", 1),
                            _createElementVNode("span", null, "(" + _toDisplayString(group.tags.length) + ")", 1)
                          ], 8, _hoisted_11)
                        ], 8, _hoisted_8),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.tags, (tag) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: tag._id,
                            class: "cursor-pointer py-2 px-3 rounded-md w-full flex items-center hover:bg-yellow-200",
                            onClick: ($event: any) => {
                      handleOptionChange(tag._id);
                      internalSelected.value.has(tag._id) ? emit('userInput') : '';
                    }
                          }, [
                            _createElementVNode("input", {
                              type: "checkbox",
                              id: `checkbox_tag_${tag._id}`,
                              value: tag._id,
                              checked: internalSelected.value.has(tag._id),
                              class: "cursor-pointer h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-600"
                            }, null, 8, _hoisted_13),
                            _createElementVNode("label", {
                              for: `checkbox_tag_${tag._id}`,
                              class: "cursor-pointer ml-3 text-sm",
                              onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"]))
                            }, _toDisplayString(tag.name), 9, _hoisted_14)
                          ], 8, _hoisted_12))
                        }), 128))
                      ])
                    ]))
                  }), 128))
                ], 2),
                (_ctx.changeOnSubmit)
                  ? (_openBlock(), _createBlock(MainButton, {
                      key: 0,
                      label: _unref(t)('buttons.apply'),
                      class: "sm:mr-0 !w-full",
                      type: "button",
                      color: "yellow",
                      onClick: ($event: any) => {
              emitChange();
              close();
            }
                    }, null, 8, ["label", "onClick"]))
                  : _createCommentVNode("", true)
              ], 512)
            ]),
            _: 1
          }, 8, ["class", "static"])
        ]),
        _: 1
      })
    ]),
    _: 3
  }))
}
}

})